import React, { useCallback, useEffect, useState } from 'react';
import { avisarSaldoCambiado } from '../../../lib/saldoBus';
import CartHeader from '../../Elements/CartHeader';
import CartRow from '../../Elements/CartRow';
import CartFooter from '../../Elements/CartFooter';
import useOverlayDismiss from '../../Elements/useOverlayDismiss';
import { formatApiError } from '../DispenserComponents/PaymentModal';
import { enviarCobro, olvidarClaveCobro, AVISO_COBRO_LENTO } from '../../../lib/helpers/claveIdempotencia';

/**
 * Club_Carrito_Credit — panel de cobro de /carritos (Figma 3638:28719).
 *
 * Sustituye al diálogo «Pagar con Saldo» en esta pantalla. El anterior enseñaba
 * subtotal, descuento y total, pero NO las líneas: había que cerrarlo y volver a
 * «Ver productos» para saber qué se estaba cobrando. Aquí el pedido se ve y se
 * corrige sin salir del cobro.
 *
 * Se compone de los tres del sistema —`CartHeader`, `CartRow` y `CartFooter`—,
 * los mismos que el carrito del dispensario, así que el socio ve lo mismo se
 * cobre donde se cobre.
 *
 * El pago es con saldo, como lo era antes: una sola llamada a `make-purchase`
 * con `payment_method: 'credit'`. Cobrar dejando el saldo en negativo está
 * permitido —el socio queda debiendo— y `CartFooter` ya lo explica sin bloquear.
 */
export default function CarritoPanel({
  /** Socio agrupado: { id, name, number, status, photo, carritos, total } */
  socio,
  /** Carritos a cobrar en esta operación (uno de un área, o todos). */
  carritos = [],
  token,
  venueId,
  onClose,
  onPaid,
  /** Editar líneas: (tx, item, delta) y (tx, item). */
  onItemQty,
  onItemRemove,
  onClear,
  onContinue,
  // #487 (punto 2): ¿hay un guardado del carrito EN VUELO en la pantalla madre?
  // Cobrar con un guardado volando = cobrar una foto que puede estar caduca.
  guardando = false,
}) {
  const [balance, setBalance] = useState(null);
  const [cobrando, setCobrando] = useState(false);
  const [error, setError] = useState('');
  const dismiss = useOverlayDismiss(onClose);

  // El saldo no viene con el carrito: se pide igual que hacía el diálogo anterior.
  const refrescarSaldo = useCallback(async () => {
    const q = (socio?.name || '').trim();
    if (!socio?.id || !venueId || !token || !q) return;
    try {
      const res = await fetch(
        `/v1/clients/search/${venueId}?q=${encodeURIComponent(q)}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const lista = await res.json();
      const yo = Array.isArray(lista) ? lista.find((c) => String(c.id) === String(socio.id)) : null;
      const bal = yo?.profile?.credit_balance ?? yo?.credit_balance;
      if (bal != null) setBalance(Number(bal));
    } catch { /* si no se puede, el pie sale sin saldo en vez de mentir */ }
  }, [socio?.id, socio?.name, venueId, token]);

  useEffect(() => { refrescarSaldo(); }, [refrescarSaldo]);

  const lineas = carritos.flatMap((tx) => (tx.items || []).map((item) => ({ tx, item })));
  const subtotal = carritos.reduce((s, t) => s + parseFloat(t.subtotal ?? t.amount ?? t.total ?? 0), 0);
  const descuento = carritos.reduce((s, t) => s + parseFloat(t.discount_amount ?? 0), 0);
  const total = carritos.reduce((s, t) => s + parseFloat(t.amount ?? t.total ?? 0), 0);
  const tipos = [...new Set(carritos.map((t) => t.cart_type))];

  const titulo = tipos.length > 1 ? 'todo' : tipos[0] === 'productos' ? 'barra' : 'dispensario';

  const validar = async () => {
    if (!socio?.id) {
      setError('Este carrito no tiene socio asignado.');
      return;
    }
    setCobrando(true);
    setError('');
    try {
      // Clave de idempotencia por carrito+socio y corte de espera: esta pantalla
      // cobraba a pelo, y por aquí volvió el doble cobro que Kong reportó el 13-09
      // (arreglado entonces solo en el Dispenser). Un reintento con la misma huella
      // devuelve la venta ya hecha en vez de cobrar otra vez.
      const res = await enviarCobro({
        token,
        venueId,
        ambito: 'carritos',
        huella: JSON.stringify([socio.id, tipos, lineas.map(({ item }) => [
          item.product_id || item.id, item.quantity, item.unit_price || item.price,
          item.real_weight ?? null, item.manual_discount ?? null,
        ])]),
        cuerpo: {
          user_id: socio.id,
          venue_id: venueId,
          items: lineas.map(({ item }) => ({
            product_id: item.product_id || item.id,
            quantity: item.quantity,
            unit_price: item.unit_price || item.price,
            real_weight: item.real_weight != null ? item.real_weight : undefined,
            location_id: item.location_id != null ? item.location_id : undefined,
            discount_pct: item.manual_discount != null ? item.manual_discount : undefined,
          })),
          payment_method: 'credit',
          // Con dos áreas el backend consume AMBOS borradores y no re-descuenta.
          cart_type: tipos.length === 1 ? tipos[0] : 'combined',
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err.detail, 'Error al procesar el pago con saldo'));
      }
      const data = await res.json();
      // Bus de saldo (plan CRUO §E): el cobro de carritos baja el saldo del socio.
      // Cobrado: la pareja huella/clave guardada ya no sirve para nada.
      olvidarClaveCobro(venueId, 'carritos');
      if (data.new_balance != null && socio?.id) avisarSaldoCambiado(socio.id, Number(data.new_balance));
      onPaid?.({
        method: 'credit',
        transactionId: data.transaction_id,
        newBalance: data.new_balance != null ? Number(data.new_balance) : undefined,
        cartTypes: tipos,
      });
    } catch (e) {
      setError(e?.name === 'AbortError' ? AVISO_COBRO_LENTO : e.message);
    } finally {
      setCobrando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Cobrar carrito">
      <div className="absolute inset-0 bg-black/20" aria-hidden {...dismiss} />

      <div className="absolute right-0 top-0 flex h-full w-full flex-col bg-[var(--background)] shadow-2xl sm:max-w-[380px]">
        <CartHeader
          tipo={titulo}
          member={{
            name: socio?.name,
            member_number: socio?.number,
            status: socio?.status,
            documents_path: socio?.photo,
          }}
          balance={balance}
          onClose={onClose}
        />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Club_table header: las columnas de la tabla del carrito. */}
          <div className="flex items-center border-b border-[var(--border)] px-4 py-2 text-[10px] font-medium uppercase tracking-wider text-[var(--muted-foreground)]">
            <span className="flex-1">Producto</span>
            <span className="w-[72px] text-center">Cantidad</span>
            <span className="w-[88px] text-right">Precio</span>
            <span className="w-12" aria-hidden />
          </div>

          <ul className="divide-y divide-[var(--border)]">
            {lineas.map(({ tx, item }, idx) => (
              <li key={`${tx.id}-${item.product_id ?? idx}`}>
                <CartRow
                  item={{
                    ...item,
                    name: item.product_name || item.name,
                    price: item.unit_price ?? item.price,
                    line_total: (Number(item.quantity) || 0) * (parseFloat(item.unit_price ?? item.price) || 0),
                  }}
                  medida={item.unit_type === 'weight' ? 'pesoFijo' : 'unidad'}
                  onQuantityChange={(n) => onItemQty?.(tx, item, n - (Number(item.quantity) || 0))}
                  onRemove={() => onItemRemove?.(tx, item)}
                />
              </li>
            ))}
          </ul>

          {error && (
            <p role="alert" className="px-4 py-3 text-xs text-[var(--destructive)]">
              {error}
            </p>
          )}
        </div>

        <CartFooter
          subtotal={subtotal}
          count={lineas.length}
          balance={balance}
          discounts={[{ label: 'Descuento', amount: descuento }]}
          total={total}
          disabled={lineas.length === 0 || cobrando || guardando}
          onCheckout={validar}
          onClear={onClear}
          onContinue={onContinue}
          className="border-t border-[var(--border)]"
        />
      </div>
    </div>
  );
}
